import { useEffect, useRef, useState } from "react";
import "./AppCustomPropertyEdit.css";
import { CreateCustomPropertyContract, CustomProperty, CustomPropertyType, RoleType, EmployeeType, UpdateCustomPropertyContract, UpdateCustomPropertyPayload } from "winslow-core";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import { MySwitch } from "../../shared/MySwitch";
import { SETTINGS_DEFAULT, SETTINGS_DISPLAY, SHORT_SETTINGS, ROLE_DISPLAY, EMPLOYEE_TYPE_DISPLAY } from "./customPropertyConfig";
import { AppCustomPropertySettings } from "./AppCustomPropertySettings";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { apiCreateCustomProperty, apiGetCustomPropertiesForWorkspace, apiGetCustomPropertyById, apiUpdateCustomProperty } from "../../../services/EmployeeRecordService";
import { useDispatch, useSelector } from "react-redux";
import { selectUIStates, selectUser, setUIStates } from "../../../redux/features/appData";
import { AppChanges } from "../app-changes/AppChanges";
import { SETTINGS_COMPONENTS } from "./AppCustomPropertyNew";
import { set } from "lodash";
import { TriangleAlert, Shield, Users, User } from "lucide-react";
import { toastSuccess } from "../../../config";

// Role icon mapping (same as in AppCustomProperties.tsx)
const ROLE_ICONS = {
    [RoleType.ADMIN]: <Shield size={16} className="role-icon" />,
    [RoleType.MANAGER]: <Users size={16} className="role-icon" />,
    [RoleType.EMPLOYEE]: <User size={16} className="role-icon" />,
};

type AppCustomPropertyEditProps = {
}

export const AppCustomPropertyEdit = ({} : AppCustomPropertyEditProps) => {

    const storeUser = useSelector(selectUser);
    const uiStates = useSelector(selectUIStates);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();

    const [hasChanges, setHasChanges] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [customPropertyId, setCustomPropertyId] = useState<string | undefined>(undefined);

    const [editingLabel, setEditingLabel] = useState(false);
    const [edittedLabel, setEdittedLabel] = useState(false);
    const editingRef = useRef(null);
    
    const [newCustomProperty, setNewCustomProperty] = useState<CustomProperty | undefined>(undefined);
    const [settings, setSettings] = useState<any>(undefined);

    const [selectedRoles, setSelectedRoles] = useState<RoleType[]>([]);
    const [selectedEmployeeTypes, setSelectedEmployeeTypes] = useState<EmployeeType[]>([]);


    useEffect(() => {
        dispatch(setUIStates({...uiStates, hrisLocation: "Edit Custom Property"}));
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", eventMouseDown);
        return () => {
            document.removeEventListener("mousedown", eventMouseDown);
        }
    }, []);

    useEffect(() => {
        (async () => {
            setCustomPropertyId(params.customPropertyId);

            const cp = await apiGetCustomPropertyById(params.customPropertyId || "");

            if (!cp) {
                toast("Error loading custom property");
                return;
            }

            setNewCustomProperty(cp);
            setSettings(JSON.parse(cp.settings || "{}"));
            setSelectedRoles(cp.visibleRoles || []);
            setSelectedEmployeeTypes(cp.visibleEmployeeTypes || []);
        })();
    }, [params.customPropertyId]);

    // useEffect(() => {
    //     const updatedCustomProperty = {
    //         ...newCustomProperty,
    //         visibleRoles: selectedRoles,
    //         visibleEmployeeTypes: selectedEmployeeTypes
    //     } as CustomProperty;
    //     setNewCustomProperty(updatedCustomProperty);
    //     setHasChanges(true);
    // }, [selectedRoles, selectedEmployeeTypes]);

    const eventMouseDown = (event: any) => {
        const current = editingRef.current as any;
        if (current && !current.contains(event.target)) {
            setEditingLabel(false);
        }
    }
    
    const handleAttributeChange = (event: any) => {
        console.log("[AppCustomPropertyEdit] value: " + event.target.value);
        console.log("[AppCustomPropertyEdit] name: " + event.target.name);

        const newProperty = {...newCustomProperty} as any;
        newProperty[event.target.name] = event.target.value;
        

        if (event.target.name === "label") {
            setEdittedLabel(true);
        }

        if (!edittedLabel && event.target.name === "display") {
            const newLabel = event.target.value.toLowerCase().replace(/\s/g, "_");
            newProperty.label = newLabel;
            setNewCustomProperty(newProperty);
        }   

        setNewCustomProperty(newProperty);
        setHasChanges(true);

    }

    const handleRequiredChange = (checked: boolean) => {
        console.log("[AppCustomPropertyEdit] required: " + checked);

        const newProperty = {...newCustomProperty} as any;
        newProperty.required = checked;

        if (newProperty.required) {
            newProperty.hidden = false;
        }

        setNewCustomProperty(newProperty);
        setHasChanges(true);
    }

    const handleHiddenChange = (checked: boolean) => {
        console.log("[AppCustomPropertyEdit] hidden: " + checked);

        const newProperty = {...newCustomProperty} as any;
        newProperty.hidden = checked;

        if (newProperty.hidden) {
            newProperty.required = false;
        }

        setNewCustomProperty(newProperty);
        setHasChanges(true);
    }

    const handleSave = async() => {
        if (!newCustomProperty) {
            toast("Error saving custom property");
            return;
        }

        console.log("[AppCustomPropertyEdit] newCustomProperty: ", newCustomProperty);  
        
        const updatedCustomProperty: CustomProperty = {
            ...newCustomProperty,
            visibleRoles: selectedRoles,
            visibleEmployeeTypes: selectedEmployeeTypes,
            settings: JSON.stringify(settings || {}),
        }

        const payload : UpdateCustomPropertyPayload = {
            customProperty: updatedCustomProperty,
        }

        const cp = await apiUpdateCustomProperty(payload);

        if (!cp) {
            toast("Error saving custom propery");
            return;
        }

        toast("Custom Property Saved", toastSuccess);
        navigate("/app/custom-properties/refresh");
    }

    const typeOptions: GenericSelectOption[] = Object.keys(CustomPropertyType)
        .sort((a: string, b:string) => {

            return SETTINGS_DISPLAY[a as CustomPropertyType].toLowerCase().localeCompare(SETTINGS_DISPLAY[b as CustomPropertyType].toLowerCase())
        })
        .map((key) => {
        return {
            displayName: SETTINGS_DISPLAY[key as CustomPropertyType],
            value: key,
            component: SETTINGS_COMPONENTS[key as CustomPropertyType]
        }
    });

    // console.log("[AppCustomPropertyEdit] typeOptions: ", typeOptions);

    const selectedTypeOption = typeOptions.find((option) => option.value === newCustomProperty?.type);
    const roleTypeOptions = Object.keys(RoleType) as string[];
    const employeeTypeOptions = Object.keys(EmployeeType) as string[];

    console.log("[AppCustomPropertyEdit] settings: ", settings);

    return (
        <div className="app-custom-property-edit">
            {newCustomProperty &&
                <>
                    <div className="app-custom-property-edit-contents">
                        <div className="app-custom-property-edit-left">
                            
                            <div className="app-custom-property-label">
                                Display Name
                            </div>
                            <div className="app-custom-property-value">
                                <input
                                    type="text"
                                    value={newCustomProperty.display}
                                    name="display"
                                    onChange={handleAttributeChange}
                                    autoFocus={true}
                                />
                            </div>

                            <div className="app-custom-property-sub-label">
                                <div className="app-custom-property-sub-label-title">
                                    Internal name:
                                </div>
                                <div 
                                    className="app-custom-property-sub-label-value"
                                    ref={editingRef}
                                >
                                    {editingLabel &&
                                        <input 
                                            type="text"
                                            value={newCustomProperty.label}
                                            name="label"
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    setEditingLabel(false);
                                                }
                                            }}
                                            onChange={handleAttributeChange}
                                        />
                                    }   
                                    {!editingLabel &&
                                        <div 
                                            onClick={() => {
                                                setEditingLabel(true);
                                            }}
                                        >
                                            {newCustomProperty.label}
                                        </div>
                                    }
                                </div>
                            </div>

                            <div className="app-custom-property-label">
                                Type
                            </div>
                            <div className="app-custom-property-value">
                                <div className="app-custom-property-edit-type">
                                    {SETTINGS_COMPONENTS[newCustomProperty.type as CustomPropertyType]}
                                    {SETTINGS_DISPLAY[newCustomProperty.type as CustomPropertyType]}
                                </div>
                                
                            </div>

                            {newCustomProperty.system &&
                                <div className="app-custom-property-system">
                                    <TriangleAlert size={16} color="orange" />
                                    This is a system property and cannot be changed.
                                </div>
                            }

                            <div className="app-custom-property-label">
                                Tab Group
                            </div>
                            <div className="app-custom-property-value">
                                <input
                                    type="text"
                                    value={newCustomProperty.group}
                                    name="group"
                                    onChange={handleAttributeChange}
                                />
                            </div>

                            <div className="app-custom-property-label">
                                Description
                            </div>
                            <div className="app-custom-property-value">
                                <textarea
                                    name="description"
                                    value={newCustomProperty.description}
                                    onChange={handleAttributeChange}
                                />
                            </div>

                            <div className="app-custom-property-label">
                                Roles
                            </div>
                            <div className="app-custom-property-value">
                                <div className={`app-custom-property-edit-roles-horizontal ${newCustomProperty.system ? 'disabled' : ''}`}>

                                    {roleTypeOptions.map((role, index) => {
                                        return (
                                            <div 
                                                key={role}
                                                className="app-custom-property-role-choice">
                                                <input 
                                                    type="checkbox"
                                                    name="visibleRoles"
                                                    checked={selectedRoles.includes(role as RoleType) || false}
                                                    disabled={newCustomProperty.system}
                                                    onChange={(event) => {
                                                        setSelectedRoles((prev) => {
                                                            if (prev.includes(role as RoleType)) {
                                                                return prev.filter((r) => r !== role);
                                                            } else {
                                                                return [...prev, role as RoleType];
                                                            }
                                                        });
                                                        setHasChanges(true);
                                                    }}
                                                />
                                                <div className="app-custom-property-role-icon">
                                                    {ROLE_ICONS[role as RoleType]}
                                                </div>
                                                <span className="app-custom-property-role-label">
                                                    {ROLE_DISPLAY[role as RoleType]}
                                                </span>
                                            </div>
                                        )
                                    }
                                    )}
                                </div>
                            </div>

                            <div className="app-custom-property-label">
                                Employee Types
                            </div>
                            <div className="app-custom-property-value">
                                <div className={`app-custom-property-edit-employee-types-two-columns ${newCustomProperty.system ? 'disabled' : ''}`}>

                                    {employeeTypeOptions.map((employeeType, index) => {
                                        return (
                                            <div 
                                                key={employeeType}
                                                className="app-custom-property-multiple-choice">
                                                <input 
                                                    type="checkbox"
                                                    name="visibleEmployeeTypes"
                                                    checked={selectedEmployeeTypes.includes(employeeType as EmployeeType) || false}
                                                    disabled={newCustomProperty.system}
                                                    onChange={(event) => {
                                                        setSelectedEmployeeTypes((prev) => {
                                                            if (prev.includes(employeeType as EmployeeType)) {
                                                                return prev.filter((et) => et !== employeeType);
                                                            } else {
                                                                return [...prev, employeeType as EmployeeType];
                                                            }
                                                        });
                                                        setHasChanges(true);
                                                    }}
                                                />
                                                <div className="app-custom-property-multiple-choice-label">
                                                    {EMPLOYEE_TYPE_DISPLAY[employeeType as EmployeeType]}
                                                </div>
                                            </div>
                                        )
                                    }
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        <div className="app-custom-property-edit-right">
                            <div className="app-custom-property-group">
                                <div className="app-custom-property-label">
                                    Required
                                </div>
                                <div className="app-custom-property-value">
                                    <MySwitch
                                        id="required"
                                        checked={newCustomProperty.required || false}
                                        onChange={handleRequiredChange}
                                        disabled={newCustomProperty.system}
                                    />
                                </div>
                            </div>

                            <div className="app-custom-property-group">
                                <div className="app-custom-property-label">
                                    Hidden
                                </div>
                                <div className="app-custom-property-value">
                                    <MySwitch
                                        id="hidden"
                                        checked={newCustomProperty.hidden || false}
                                        onChange={handleHiddenChange}
                                    />
                                </div>
                            </div>

                            <AppCustomPropertySettings 
                                type={newCustomProperty.type || CustomPropertyType.SHORT}
                                settings={settings}
                                onChange={(newSettings, disabled) => {
                                    // console.log("[AppCustomPropertyEdit] new settings: ", newSettings);
                                    console.log("[AppCustomPropertyEdit] disabled: ", disabled);
                                    setSettings({...newSettings});
                                    setHasChanges(true);
                                    setDisabled(disabled);
                                }}
                            />
                        
                        </div>
                    </div>
                </>
        }

            {hasChanges &&
                <AppChanges
                    saveDisabled={(newCustomProperty?.display === "") || disabled}
                    onCancel={() => {
                        navigate("/app/custom-properties/");
                    }}
                    onSubmit={handleSave}
                />
            }

            {/* <div className="app-custom-property-actions">
                <button 
                    className="button-cancel"
                    onClick={() => {
                        navigate("/app/custom-properties/");
                    }}
                > 
                    Cancel
                </button>

                <button 
                    className="button-save"
                    onClick={handleSave}
                >
                    Save
                </button> */}
            {/* </div> */}
        </div>
    )
}